import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { PqIndexEvent } from "@/lib/types";

// Archive-wide PQ event index — reads /api/pq/analysis/index (index.parquet,
// built by scripts/build_sidecar.py). Admin-only. A filter/scan surface:
// narrow the archive by date or by a minimum THD / peak current / unbalance.
//
// Rows are keyed by the build_sidecar event_id (uuid derived from .cvf path),
// which is not the pq_events DB id — so rows are not deep-linked to the event
// detail page; this view is for scanning, not navigation.

type IndexResponse = {
  events: PqIndexEvent[];
  built: boolean;
  count?: number;
  detail?: string;
};

function fmt(v: number | null | undefined, digits = 2, unit = ""): string {
  if (v == null) return "—";
  return `${v.toFixed(digits)}${unit}`;
}

function basename(path: string): string {
  const parts = path.split("/");
  return parts[parts.length - 1] || path;
}

export function PqIndex() {
  const [resp, setResp] = useState<IndexResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filters — empty string = inactive.
  const [dateText, setDateText] = useState("");
  const [minVThd, setMinVThd] = useState("");
  const [minIThd, setMinIThd] = useState("");
  const [minPeakI, setMinPeakI] = useState("");
  const [minUnbalance, setMinUnbalance] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get<IndexResponse>("/api/pq/analysis/index")
      .then((r) => !cancelled && setResp(r))
      .catch((e) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError ? `Error ${e.status}` : String(e)
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    if (!resp?.events) return [];
    const vThd = parseFloat(minVThd);
    const iThd = parseFloat(minIThd);
    const peakI = parseFloat(minPeakI);
    const unbal = parseFloat(minUnbalance);
    const passMin = (v: number | null, min: number) =>
      isNaN(min) || (v != null && v >= min);
    return resp.events
      .filter((e) => {
        if (dateText && !(e.capture_date ?? "").includes(dateText)) return false;
        if (!passMin(e.v_thd_avg_pct, vThd)) return false;
        if (!passMin(e.i_thd_avg_pct, iThd)) return false;
        if (!passMin(e.peak_i_any_phase, peakI)) return false;
        if (!passMin(e.v_unbalance_pct, unbal)) return false;
        return true;
      })
      .sort((a, b) => (b.capture_date ?? "").localeCompare(a.capture_date ?? ""));
  }, [resp, dateText, minVThd, minIThd, minPeakI, minUnbalance]);

  if (error) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">PQ Index</h1>
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }
  if (!resp) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (!resp.built) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">PQ Index</h1>
        <p className="text-sm text-muted-foreground">
          {resp.detail ??
            "The harmonic index has not been built yet. Run scripts/build_sidecar.py."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">PQ Index</h1>
        <p className="text-sm text-muted-foreground">
          {rows.length} of {resp.events.length} analysed events
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <label className="space-y-1 text-xs text-muted-foreground">
              Capture date contains
              <Input
                value={dateText}
                onChange={(e) => setDateText(e.target.value)}
                placeholder="2026-05"
              />
            </label>
            <label className="space-y-1 text-xs text-muted-foreground">
              Min V THD %
              <Input
                type="number"
                value={minVThd}
                onChange={(e) => setMinVThd(e.target.value)}
              />
            </label>
            <label className="space-y-1 text-xs text-muted-foreground">
              Min I THD %
              <Input
                type="number"
                value={minIThd}
                onChange={(e) => setMinIThd(e.target.value)}
              />
            </label>
            <label className="space-y-1 text-xs text-muted-foreground">
              Min peak I (A)
              <Input
                type="number"
                value={minPeakI}
                onChange={(e) => setMinPeakI(e.target.value)}
              />
            </label>
            <label className="space-y-1 text-xs text-muted-foreground">
              Min V unbalance %
              <Input
                type="number"
                value={minUnbalance}
                onChange={(e) => setMinUnbalance(e.target.value)}
              />
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Capture date</TableHead>
                <TableHead>File</TableHead>
                <TableHead className="text-right">Ch</TableHead>
                <TableHead className="text-right">V THD</TableHead>
                <TableHead className="text-right">I THD</TableHead>
                <TableHead className="text-right">V unbal</TableHead>
                <TableHead className="text-right">Peak I</TableHead>
                <TableHead className="text-right">Dur</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.event_id}>
                  <TableCell className="tabular-nums">
                    {e.capture_date ?? "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {basename(e.cvf_path)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {e.n_channels ?? "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(e.v_thd_avg_pct, 2, "%")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(e.i_thd_avg_pct, 2, "%")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(e.v_unbalance_pct, 2, "%")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(e.peak_i_any_phase, 1, " A")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(e.capture_duration_s, 3, " s")}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-6">
                    No events match the filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
